import { useState, useEffect, useMemo } from 'react';
import Actions from '../../../layouts/adminLayouts/Actions/Actions';
import Content from '../../../layouts/adminLayouts/Content/Content';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import Order from '../../../admin/Order/Order';
import Card from '../../../UI/Card/Card';
import Loader from '../../../UI/Loader/Loader';
import Placeholder from '../../../UI/Placeholder/Placeholder';
import { NO_ORDERS } from '../../../../constants/messages';
import OrderEditorPopup from '../../../admin/Order/OrderEditorPopup/OrderEditorPopup';
import classes from './OrdersPage.module.css';
import { Delivery, ThreePlusPromoMeta, Order as OrderModel } from '../../../../types/common';
import Button from '../../../UI/Button/Button';
import { exportOrdersToExcel } from '../../../../utils/exportToExcel';
import { exportSingleOrderToExcel } from '../../../../utils/exportSingleOrderToExcel';

type SecondPillowPromoMeta = {
    key: string;
    discountAmount: number;
    validUntil?: string | null;
    categoryUrl?: string | null;
};
interface OrderData {
    id: string;
    user: RootState['common']['orders'][0]['user'];
    orderNumber: string;
    orderStatus: string;
    delivery: Delivery | null;
    timestamp: number;
    totalPrice: number;
    cart: OrderModel['cart'];
    totalDiscount: number;
    totalWeight: number;
    birthdayDiscount: boolean;
    paidWithPoints: number | null;
    orderComment: string;
    promoCode?: string | null;
    hasPromo?: boolean;
    threePlusPromo?: ThreePlusPromoMeta | null;
    secondPillowPromo?: SecondPillowPromoMeta | null;
}

type Filter = 'all' | 'paid' | 'assembled' | 'completed' | 'cancelled' | 'notCancelled' | 'cancel_pending';
const isFilter = (v: string): v is Filter =>
    (['all', 'paid', 'assembled', 'completed', 'cancelled', 'notCancelled', 'cancel_pending'] as const).includes(v as Filter);

const filterOptions = [
    { value: 'all', label: 'все заказы' },
    { value: 'paid', label: 'только оформленные' },
    { value: 'assembled', label: 'только собранные' },
    { value: 'completed', label: 'только завершенные' },
    { value: 'notCancelled', label: 'кроме отменённых' },
    { value: 'cancel_pending', label: 'отмена в процессе' },
    { value: 'cancelled', label: 'только отменённые' },
];

const OrdersPage: React.FC = () => {
    const { orders, isLoading } = useSelector((state: RootState) => state.common);
    const [editedOrder, setEditedOrder] = useState<OrderData | null>(null);
    const [scrollY, setScrollY] = useState(0);
    const [filter, setFilter] = useState<Filter>('paid');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const sortedOrders = useMemo(() => [...orders].sort((a, b) => b.timestamp - a.timestamp), [orders]);
    const paidOrders = useMemo(() => sortedOrders.filter((o) => o.orderStatus === 'paid'), [sortedOrders]);
    const hasPaidOrders = paidOrders.length > 0;

    const filteredOrders = useMemo(() => {
        return sortedOrders.filter((o) => {
            switch (filter) {
                case 'paid':
                    return o.orderStatus === 'paid';
                case 'assembled':
                    return o.orderStatus === 'assembled';
                case 'completed':
                    return o.orderStatus === 'completed';
                case 'cancelled':
                    return o.orderStatus === 'cancelled';
                case 'cancel_pending':
                    return o.orderStatus === 'cancel_pending';
                case 'notCancelled':
                    return o.orderStatus !== 'cancelled' && o.orderStatus !== 'cancel_pending';
                default:
                    return true;
            }
        });
    }, [sortedOrders, filter]);

    const handleEditOrder = (orderData: OrderData) => {
        setScrollY(window.scrollY || window.pageYOffset);
        setEditedOrder(orderData);
    };

    const handleCloseEditor = () => {
        setEditedOrder(null);
    };

    const handleExport = async () => {
        try {
            await exportOrdersToExcel(paidOrders);
        } catch (e) {
            console.error('Ошибка экспорта', e);
        }
    };

    const toggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const copy = new Set(prev);
            if (checked) copy.add(id);
            else copy.delete(id);
            return copy;
        });
    };

    const anySelected = selectedIds.size > 0;

    const handleExportSelected = async () => {
        try {
            const toExport = sortedOrders.filter((o) => selectedIds.has(o.id));
            for (const order of toExport) {
                await exportSingleOrderToExcel(order);
            }
        } catch (e) {
            console.error('Ошибка экспорта выбранных заказов', e);
        }
    };

    useEffect(() => {
        setSelectedIds(new Set());
    }, [filter]);

    useEffect(() => {
        if (editedOrder) {
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
        }
    }, [editedOrder, scrollY]);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                    <Actions title="Заказы" />
                    <select
                        className={classes.orders__filter}
                        value={filter}
                        onChange={(e) => {
                            const val = e.target.value;
                            setFilter(isFilter(val) ? val : 'all');
                        }}
                    >
                        {filterOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                    {anySelected && (
                        <Button mode={'secondary'} onClick={handleExportSelected} isDisabled={isLoading}>
                            Выгрузить выбранные
                        </Button>
                    )}
                    <Button mode={'secondary'} onClick={handleExport} isDisabled={!hasPaidOrders || isLoading}>
                        Выгрузить в Excel
                    </Button>
                </div>
            </div>

            <Content>
                <>
                    {isLoading && <Loader />}
                    {!isLoading && (
                        <Card adminTable>
                            <>
                                <table className={classes.orders__table}>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Номер заказа</th>
                                            <th>Статус</th>
                                            <th>Дата</th>
                                            <th>Покупатель</th>
                                            <th>Телефон</th>
                                            <th>Адрес</th>
                                            <th>Сумма</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.length === 0 && (
                                            <tr>
                                                <td colSpan={8}>
                                                    <Placeholder text={NO_ORDERS} />
                                                </td>
                                            </tr>
                                        )}

                                        {filteredOrders.map(
                                            ({
                                                user,
                                                orderNumber,
                                                orderStatus,
                                                delivery = null,
                                                orderComment,
                                                totalPrice,
                                                totalWeight,
                                                birthdayDiscount,
                                                paidWithPoints,
                                                totalDiscount,
                                                cart,
                                                timestamp,
                                                id,
                                                promoCode,
                                                threePlusPromo,
                                                secondPillowPromo,
                                            }) => {
                                                const isSelectable = orderStatus !== 'cancelled' && orderStatus !== 'completed' && orderStatus !== 'cancel_pending';
                                                const selected = selectedIds.has(id);
                                                return (
                                                    <Order
                                                        key={id}
                                                        id={id}
                                                        user={user}
                                                        orderNumber={orderNumber}
                                                        orderStatus={orderStatus}
                                                        delivery={delivery}
                                                        orderComment={orderComment}
                                                        cart={cart}
                                                        totalPrice={totalPrice}
                                                        timestamp={timestamp}
                                                        totalWeight={totalWeight}
                                                        birthdayDiscount={birthdayDiscount}
                                                        paidWithPoints={paidWithPoints}
                                                        totalDiscount={totalDiscount}
                                                        hasPromo={!!promoCode}
                                                        promoCode={promoCode}
                                                        threePlusPromo={threePlusPromo}
                                                        secondPillowPromo={secondPillowPromo as any}
                                                        onEdit={handleEditOrder}
                                                        selectable={isSelectable}
                                                        selected={selected}
                                                        onSelectChange={(checked) => toggleSelect(id, checked)}
                                                    />
                                                );
                                            },
                                        )}
                                    </tbody>
                                </table>
                            </>
                        </Card>
                    )}
                </>
            </Content>
            {editedOrder && <OrderEditorPopup orderData={editedOrder} onClose={handleCloseEditor} />}
        </>
    );
};

export default OrdersPage;
